import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { auth } from "@/config/firebase";
import { AuthStalledScreen } from "@/components";
import { COLORS } from "@/constants";
import { useLongPressVConsole } from "@/hooks";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, stalled, error, retryProfile } = useAuth();
  const location = useLocation();
  const resolvedUser = user ?? auth.currentUser;
  const longPressVConsoleHandlers = useLongPressVConsole();

  // Show loading while checking authentication
  if (loading) {
    // The auth bootstrap has been hanging past the watchdog window — a dead
    // connection or a request that never settles. Nothing will unstick it on
    // its own, so offer a reload instead of an eternal "Loading...".
    if (stalled) {
      return (
        <AuthStalledScreen longPressHandlers={longPressVConsoleHandlers} />
      );
    }
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100dvh",
          color: COLORS.text.pagePrimary,
        }}
      >
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!resolvedUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // The session is valid but the profile fetch failed (backend down, network
  // error). Rendering pages with a null profile crashes or shows blanks —
  // surface the failure with an in-place retry instead.
  if (error) {
    return (
      <AuthStalledScreen
        longPressHandlers={longPressVConsoleHandlers}
        message={error}
        onRetry={() => {
          void retryProfile();
        }}
      />
    );
  }

  // Render children if authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
